import './ResetPasswordView.css'

import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router'
import { useAtomValue, useSetAtom } from 'jotai'

import type { FormValidator } from '../components/FormValidators'

import { session } from '../store'

import { Button } from '../components/Button'
import { FieldError } from '../components/FieldError'
import { Form } from '../components/Form'
import { TextField } from '../components/TextField'
import { useFormFieldErrors } from '../hooks/useFormFieldErrors'

type FormValue = {
  newPassword: string
  confirmPassword: string
}

const validatePasswordMatch: FormValidator<FormValue> = (value) => {
  if (value.confirmPassword && value.newPassword !== value.confirmPassword) {
    return { confirmPassword: 'mismatch' }
  }
  return undefined
}

export function ResetPasswordView() {
  const [searchParams] = useSearchParams()
  const [token] = useState(() => searchParams.get('token') || '')
  const [formValue, setFormValue] = useState<FormValue>({ newPassword: '', confirmPassword: '' })
  const isLoading = useAtomValue(session.resetPasswordIsLoadingAtom)
  const success = useAtomValue(session.resetPasswordSuccessAtom)
  const confirmPasswordReset = useSetAtom(session.resetPasswordConfirmAtom)
  const resetResetPassword = useSetAtom(session.resetResetPasswordAtom)
  const navigate = useNavigate()

  const serverErrors = useFormFieldErrors(session.resetPasswordIssuesAtom)

  useEffect(() => {
    if (success) {
      const timeout = setTimeout(() => {
        navigate('/login', { replace: true })
      }, 3000)
      return () => clearTimeout(timeout)
    }
    return undefined
  }, [success, navigate])

  useEffect(() => {
    return () => {
      resetResetPassword()
    }
  }, [resetResetPassword])

  const handleSubmit = () => {
    confirmPasswordReset(token, formValue.newPassword)
  }

  if (!token) {
    return (
      <div className="reset-password-view reset-password-view__message">
        <h2 className="reset-password-view__message-title">Invalid reset link</h2>
        <p className="reset-password-view__message-text">
          The password reset link is invalid or missing. Please request a new password reset.
        </p>
        <Link to="/forgot-password" className="reset-password-view__message-link">
          Request password reset
        </Link>
      </div>
    )
  }

  if (success) {
    return (
      <div className="reset-password-view reset-password-view__message">
        <h2 className="reset-password-view__message-title">Password reset successful</h2>
        <p className="reset-password-view__message-text">
          Your password has been successfully reset. You will be redirected to the sign in page in a
          few seconds.
        </p>
        <Link to="/login" className="reset-password-view__message-link">
          Go to sign in
        </Link>
      </div>
    )
  }

  return (
    <Form
      className="reset-password-view"
      value={formValue}
      onChange={setFormValue}
      onSubmit={handleSubmit}
      flags={serverErrors}
      validate={validatePasswordMatch}
    >
      <h2 className="reset-password-view__title">Reset your password</h2>
      <p className="reset-password-view__subtitle">Enter your new password below</p>

      <TextField
        name="newPassword"
        label="New Password"
        kind="password"
        placeholder="Enter new password"
        required
        min={8}
        disabled={isLoading}
      />
      <FieldError
        name="newPassword"
        text={{
          required: 'Password is required',
          min: 'Password must be at least 8 characters',
          tooShort: 'Password must be at least 8 characters',
          tooLong: 'Password is too long',
        }}
      />

      <TextField
        name="confirmPassword"
        label="Confirm Password"
        kind="password"
        placeholder="Confirm new password"
        required
        disabled={isLoading}
      />
      <FieldError
        name="confirmPassword"
        text={{
          required: 'Please confirm your password',
          mismatch: 'Passwords do not match',
        }}
      />

      <FieldError
        name="token"
        text={{
          required: 'Token is required',
          invalid: 'Invalid or expired reset link',
          expired: 'Reset link has expired',
          alreadyUsed: 'Reset link has already been used',
          networkError: 'Network error. Please try again.',
        }}
      />

      <div className="reset-password-view__actions">
        <Button type="submit" kind="primary" disabled={isLoading}>
          {isLoading ? 'Resetting password...' : 'Reset Password'}
        </Button>
        <Link to="/login" className="reset-password-view__alt-action">
          Back to sign in
        </Link>
      </div>
    </Form>
  )
}
